import React, { useEffect, useState } from 'react';
import { CcfBadge, CcfTooltip, useTranslator } from '@nice-ccf/ui-controls';
import { Navigation } from '../../enums/navigation-menus';
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedMenuName, globalActions } from '../global.app.slice';
import { createStyles, makeStyles } from '@mui/styles';
import { Theme, useTheme, useMediaQuery, Box } from '@mui/material';
import { NavigationItem } from '../ccf-navigation/useNavigationItems';
import { useHistory } from 'react-router-dom';
import { ListItemButton } from '@mui/material';
import {
  getContactsCount,
  getNewCommitment,
  setNewCommitment,
} from '../ccf-inbox/ccf-inbox.slice';

const useStyles = makeStyles(({ palette, spacing }: Theme) =>
  createStyles({
    navItem: {
      cursor: 'pointer',
    },
    menuItemAlignment: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    sidebarItem: {
      width: '100%',
      height: 43,
      margin: 0,
      padding: 0,
      display: 'flex',
      justifyContent: 'center',
    },
    sidebarItemSelected: {
      '&.MuiListItemButton-root': {
        backgroundColor: `rgba(0, 124, 190, 0.12) !important`,
        borderRadius: '2px',
      },
    },
    sidebarItemBadge: {
      // backgroundColor: palette.primary.main,
      // color: palette.background.paper,
      height: '16px !important',
      minWidth: '16px !important',
      fontSize: '9px !important',
      padding: '1px 2px 0px 2px !important',
      border: '2px solid #f2f2f2',
      background: '#007cbe 0% 0% no-repeat padding-box',
      color: '#ffffff',
    },
    unreadItemBadge: {
      height: '12px !important',
      minWidth: '12px !important',
      fontSize: '9px !important',
      padding: '1px 2px 0px 2px !important',
      border: '2px solid #f2f2f2',
      background: '#E4263C 0% 0% no-repeat padding-box',
      color: '#ffffff',
    },
    sidebarItemBadgeStandard: {
      height: spacing(2),
      minWidth: spacing(2),
      fontSize: '0.5em',
      padding: '1px 2px 0px 2px',
      border: `2px solid ${palette.background.default}`,
      position: 'absolute',
      top: '8px',
      left: '4px',
    },
    sidebarItemNetworkInformation: {
      top: '4px',
      right: '4px',
      height: '15.2px',
      position: 'absolute',
      fontSize: '0.5em',
      width: '16px',
    },
    sidebarItemNetworkInformationWarning: {
      color: '#f9a825',
    },
    sidebarItemNetworkInformationDanger: {
      color: '#d32f2f',
    },
    networkSpeed: {
      outline: 0,
      border: 0,
      borderRadius: '500px',
      width: '98px',
      transition: 'box-shadow 0.2s ease-in-out',
      overflow: 'hidden',
      height: '9.64px',
      marginTop: '10.68px',
      '-webkit-appearance': 'none',
      backgroundImage: `linear-gradient(to right, #66bb6a, #f9a825, #d32f2f)`,
      '&::-webkit-slider-runnable-track': {
        height: '12px',
        '-webkit-appearance': 'none',
        color: `${palette.background.default}`,
        transition: 'box-shadow 0.2s ease-in-out',
      },
      '&::-webkit-slider-thumb': {
        width: '12px',
        '-webkit-appearance': 'none',
        height: '12px',
        cursor: 'ew-resize',
        background: 'inherit',
        boxShadow: '-340px 0 0 320px transparent, inset 0 0 0 40px transparent',
        border: `2px solid ${palette.background.paper}`,
        borderRadius: '50%',
        transition: 'box-shadow 0.2s ease-in-out',
        position: 'relative',
      },
      '&:active::-webkit-slider-thumb': {
        background: 'inherit',
      },
    },
    networkStatusLabel: {
      fontSize: '12px',
      lineHeight: '125%',
      color: `#808080`,
    },
  })
);

/**
 * Used to add navigation item to CcfAppNavigation
 * @example `<CcfNavItem />`
 * @param menuName - menuName must be listed in the CcfTranslationKey object and in lowercase
 * @param showBadge - This is an optional parameter that will add a Badge to an icon
 * @param className - optional className wrapping the icon after the tooltip
 * @param onClick - this parameter will override the existing onClick functionality
 * @param toolTipPlacement - 'A string to determine placement for tooltip'
 */
export const CcfNavItem: React.FC<NavigationItem> = ({
  menuName,
  className,
  onClick,
  children,
  toolTipPlacement = 'right',
  icon,
  tooltip,
}) => {
  const dispatch = useDispatch();
  const selectedMenu = useSelector(getSelectedMenuName);
  const [translate] = useTranslator();
  const { setSelectedMenu } = globalActions;
  const styles = useStyles();
  const theme: Theme = useTheme();
  const isSmView = useMediaQuery(theme.breakpoints.down('xl'));
  const [show, setShow] = useState<boolean>(false);
  const commitmentState = useSelector(getNewCommitment);
  const inboxCount = useSelector(getContactsCount);
  /**
   * Used to swtich menus
   * @param name - string
   * @example - setMenu(Navigation.HOME)
   */
  const setMenu = (name: Navigation, path: string) => {
    dispatch(setSelectedMenu({ name: name }));
    if (commitmentState) {
      dispatch(setNewCommitment(false));
    }
  };

  //   /**
  //    * Used to get badge count for menus
  //    * @param name - string
  //    * @example - menuBadgeCount(Navigation.ASSIGNMENT)
  //    */
  //   const menuBadgeCount = (name: Navigation) => {
  //     switch(name){
  //     case Navigation.ASSIGNMENT:
  //       return 10;
  //     case Navigation.QUEUE:
  //       return totalContactsCount;
  //     default:
  //       return;
  //     }
  //   }

  /**
   * Function to be called when QR tab pressed
   * @param e - event object
   * @example - clickHandler()
   */
  const clickHandler = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setMenu(menuName, `${menuName}`);
  };

  return (
    //   Do not remove any DIV's.. For some reason MUI needs them for tooltip
    <div className={styles.navItem} id={`agent_leftnav_${menuName}`}>
      <CcfTooltip
        title={
          menuName === Navigation.SETTINGS ? (
            <Box sx={{ backgroundColor: theme.palette.background.paper }}>
              <div className={styles.networkStatusLabel}>
                {translate('networkstatus')}
              </div>
              <input
                className={styles.networkSpeed}
                type="range"
                min="0"
                max="4"
                step="0.04"
                value={80}
              />
            </Box>
          ) : (
            tooltip
          )
        }
        arrow
        placement={toolTipPlacement}
        tooltipForRTL="ccfTooltipRight0"
        aria-label={menuName}
        open={show}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(false)}
        data-testid="tooltip"
        styles={
          menuName === Navigation.SETTINGS
            ? {
                ccfTooltipArrow: {
                  color: theme.palette.background.paper,
                },
                ccfTooltip: {
                  borderRadius: '8px',
                  width: '120px',
                  height: '60px',
                  backgroundColor: `${theme.palette.background.paper}`,
                  boxShadow: '0px 5px 15px 0px rgb(0 0 0 / 30%)',
                  padding: '8px 12px',
                },
              }
            : undefined
        }
      >
        <div className={className}>
          <ListItemButton
            className={styles.menuItemAlignment}
            selected={selectedMenu === menuName}
            onClick={(e) => clickHandler(e)}
            classes={{
              gutters: styles.sidebarItem,
              selected: styles.sidebarItemSelected,
            }}
            style={{
              padding: '0px',
              display: 'flex',
              justifyContent: 'center',
            }}
            tabIndex={0}
            onKeyUp={(e) => {
              if (e.key === 'Enter') setMenu(menuName, `${menuName}`);
            }}
            data-testid="list-item"
          >
            {menuName === Navigation.INTERACTION && inboxCount > 0 ? (
              <CcfBadge
                label="contactsCount"
                classes={{
                  badge: styles.sidebarItemBadge,
                }}
                badgeContent={inboxCount}
              >
                <div>
                  {icon}
                  {children}
                </div>
              </CcfBadge>
            ) : menuName === Navigation.AGENT_MESSAGES &&
              selectedMenu !== Navigation.AGENT_MESSAGES ? (
              <CcfBadge
                label="agentMessages"
                classes={{
                  badge: styles.unreadItemBadge,
                }}
                badgeContent=""
              >
                <div>
                  {icon}
                  {children}
                </div>
              </CcfBadge>
            ) : (
              <div>
                {icon}
                {children}
              </div>
            )}
          </ListItemButton>
        </div>
      </CcfTooltip>
    </div>
  );
};
